/**
 * Token custody.
 *
 * The access token lives in memory only, so an XSS payload cannot lift a
 * long-lived credential out of storage. The refresh token is persisted so a
 * reload does not log the player out; it is the deliberate trade-off this app
 * makes for a closed portal without cookie-based sessions.
 */

const REFRESH_TOKEN_KEY = 'robu.refresh-token';

let accessToken: string | null = null;

type Listener = () => void;
const sessionEndedListeners = new Set<Listener>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getRefreshToken(): string | null {
  try {
    return window.localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    // Private-mode Safari and hardened browsers can throw on storage access.
    return null;
  }
}

export function setRefreshToken(token: string | null): void {
  try {
    if (token === null) window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    else window.localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch {
    /* Storage is unavailable; the session simply will not survive a reload. */
  }
}

export function clearTokens(): void {
  accessToken = null;
  setRefreshToken(null);
}

/** Notified when the refresh token stops working and the user must log in. */
export function onSessionEnded(listener: Listener): () => void {
  sessionEndedListeners.add(listener);
  return () => sessionEndedListeners.delete(listener);
}

export function emitSessionEnded(): void {
  for (const listener of sessionEndedListeners) listener();
}
